/**
 *
 * @module product
 *
 */

import Check from 'check-types';
import Services from './../services/entry';
import ProductEntitlement from './productEntitlement';
import ProductStatus from './productStatus';

const ErrorReason = Services.Exception.ErrorReason;
const ProductRule = Services.Subscription.ProductRule;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    array,
    nonEmptyObject,
    nonEmptyString,
    number
} = Check.assert;

/**
 *
 * @class Product
 * @desc Represents a product a user is subscribed to.
 *
 */
export default class Product {

    /**
     *
     * @param {Object} options
     * @param {Array<String>} [options.categoryCodes=[]]
     * @param {Object} [options.error=null]
     * @param {Number} [options.id=undefined]
     * @param {String} [options.name=undefined]
     * @param {String} [options.partner=undefined]
     * @param {Array<Object>} [options.productEntitlements=[]]
     * @param {Object} [options.rule=null]
     * @param {String} options.sku
     * @param {String} options.status
     *
     */
    constructor(options) {

        const errorMessage = {
            ctor: 'Product(options)',
            get options() {
                return `${this.ctor} options should be an Object`;
            },
            get categoryCodes() {
                return `${this.ctor} options.categoryCodes should be an Array of Strings`;
            },
            get error() {
                return `${this.ctor} options.error should be an Object`;
            },
            get id() {
                return `${this.ctor} options.id should be a Number`;
            },
            get name() {
                return `${this.ctor} options.name should be a String with length`;
            },
            get partner() {
                return `${this.ctor} options.partner should be a String with length`;
            },
            get productEntitlements() {
                return `${this.ctor} options.productEntitlements should be an Array of Objects`;
            },
            get rule() {
                return `${this.ctor} options.rule should be an Object`;
            },
            get sku() {
                return `${this.ctor} options.sku should be a String with length`;
            },
            get status() {
                return `${this.ctor} options.status should be a String with length`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { categoryCodes = [], error, id, name, partner, productEntitlements = [], rule, sku, status } = options;

        nonEmptyString(sku, errorMessage.sku, InvalidArgumentException);
        nonEmptyString(status, errorMessage.status, InvalidArgumentException);
        array.of.nonEmptyString(categoryCodes, errorMessage.categoryCodes, InvalidArgumentException);

        let errorReason = null;

        if (Check.assigned(error) && Check.assigned(error.code) && Check.assigned(error.description)) {
            errorReason = new ErrorReason(error.code, error.description);
        }

        if (Check.assigned(id)) {
            number(id, errorMessage.id, InvalidArgumentException);
        }

        if (Check.assigned(name)) {
            nonEmptyString(name, errorMessage.name, InvalidArgumentException);
        }

        if (Check.assigned(partner)) {
            nonEmptyString(partner, errorMessage.partner, InvalidArgumentException);
        }

        array.of.nonEmptyObject(productEntitlements, errorMessage.productEntitlements, InvalidArgumentException);

        if (Check.assigned(rule)) {
            nonEmptyObject(rule, errorMessage.rule, InvalidArgumentException);
        }

        /**
         *
         * @access public
         * @type {Array<String>}
         * @desc An array of category codes associated with the product.
         *
         */
        this.categoryCodes = categoryCodes;

        /**
         *
         * @access public
         * @type {Number|undefined}
         * @desc Gets the ID.
         *
         */
        this.id = id;

        /**
         *
         * @access public
         * @type {String|undefined}
         * @desc Gets the name of the product.
         *
         */
        this.name = name;

        /**
         *
         * @access public
         * @type {String}
         * @desc Gets the SKU.
         *
         */
        this.sku = sku;

        /**
         *
         * @access public
         * @type {SDK.Subscription.ProductStatus}
         *
         */
        this.status = ProductStatus[status] || ProductStatus.UNKNOWN;

        /**
         *
         * @access public
         * @type {SDK.Services.Exception.ErrorReason|null}
         * @desc Gets the error reason if one is associated with the product.
         *
         */
        this.error = errorReason;

        /**
         *
         * @access public
         * @type {Array<SDK.Subscription.ProductEntitlement>}
         * @desc Gets an array of product entitlements.
         *
         */
        this.entitlements = productEntitlements.map((entitlement) => new ProductEntitlement(entitlement));

        /**
         *
         * @access private
         * @type {String|undefined}
         * @desc Gets the partner associated with the product.
         *
         */
        this.partner = partner;

        /**
         *
         * @access private
         * @type {SDK.Services.Subscription.ProductRule|null}
         * @desc Gets the product rule.
         *
         */
        this.rule = rule ? new ProductRule(rule) : null;

    }

}



// WEBPACK FOOTER //
// ./src/subscription/product.js